import './searchFilter.scss';
import React, { useEffect, useState } from 'react';
import {
    equals,
    isEmpty,
    not,
    assoc,
    curry,
    defaultTo,
    find,
    map,
    pipe,
    prop,
    propEq
} from 'ramda';
import { FilterImageCheckbox } from '../filterImageCheckbox/FilterImageCheckbox';
import { ModalActionButtons } from './ModalActionButtons';
import Slider from '../slider/Slider';
import { ErrorBoundary } from './ErrorBoundary';
import { Tabs } from './Tabs';

import Loader from '../loader/Loader';
import helpers from '../../js/helpers';
import { useTranslation } from 'react-i18next';
import TrashIcon from '@icons/trash.svg';
import { Pages } from '../pages/Pages';
import { CheckboxGrid } from '../checkboxGrid/CheckboxGrid';
import type {
    ElasticAggregations,
    SearchFilterProps,
    SearchFilterState,
    CarType,
    CarModel
} from '../../types/index';
import { CarModelSelector } from '../carModelSelector/CarModelSelector';
import { FilterCloseButton } from '../filterCloseButton/FilterCloseButton';
import {useNormalizedTransformer} from "../../hooks/useNormalizedTransformer";

const getCount = (arr: any, key: any) =>
    pipe(find(propEq('key', key)), prop('count'), defaultTo(0))(arr);

const attachAggregationCountFrom = curry((arr, item) =>
    assoc('count', getCount(arr, item.key), item)
);

/**
 * SearchFilter props explainer:
 * @param {isSimplifiedView} props.isSimplifiedView when set to true only car models & car types can be used
 * @param {boolean} props.isScrollIntoViewEnabled when set to true, when selecting car models, car types will be scrolled into the view
 * @param {config} props.config be used to configure the component for different consuming applications (RCM, content.audi)
 */
export const SearchFilter = (props: SearchFilterProps) => {
    const { t } = useTranslation();
    const normalizeTag = useNormalizedTransformer([
        {
            search: /^.*?:/g,
            replace: ''
        },
        {
            search: /[_-]+/g,
            replace: ' '
        }
    ]);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedOptions, setSelectedOptions] = useState<
        SearchFilterState['selectedOptions']
    >({
        carTypes: [],
        fileExtensions: [],
        modelYears: { high: null, low: null },
        carModels: [],
        tags: [],
        kvps: []
    });
    const [options, setOptions] = useState<SearchFilterState['options']>({
        carTypes: [],
        fileExtensions: {
            document: [],
            image: [],
            video: []
        },
        productionYears: [],
        carModels: [],
        tags: [],
        paginatedTags: [],
        paginatedKvps: []
    });

    const combineCarTypeAggregatesWithModels = (carModels: CarModel[]): any =>
        carModels.map(model => ({
            ...model,
            count:
                props.searchAggregations.carModels?.find(m => m.key === model.key)
                    ?.count ?? 0,
            carTypes: model.carTypes.map(modelCarType => ({
                ...modelCarType,
                count:
                    props.searchAggregations.carTypes?.find(
                        carType => carType.key === modelCarType.key
                    )?.count || 0
            }))
        }));

    const buildFilterOptions = () => {
        const { searchAggregations, carModels, carTypes: allCarTypes } = props;

        const selectedModels = selectedOptions.carModels;

        if (!isEmpty(searchAggregations)) {
            let carTypes = allCarTypes;

            if (selectedModels && selectedModels.length > 0) {
                carTypes = selectedModels
                    .reduce<CarType[]>(
                        (acc, currentValue) =>
                            acc.concat(
                                // weird ts bug...
                                // @ts-ignore
                                carModels.find(m => m.model === currentValue)
                                    .carTypes
                            ),
                        []
                    )
                    .map(carType => ({ ...carType, key: carType.type }));
            }

            /**
             * We nullify filter options if those do not exists.
             * The SearchFilter component only shows options that exists in filterOptions
             */
            setIsLoading(false);
            setOptions({
                ...searchAggregations,
                carModels: searchAggregations && searchAggregations.carModels
                    ? combineCarTypeAggregatesWithModels(carModels)
                    : undefined,
                carTypes: searchAggregations && searchAggregations.carTypes
                    ? map(
                          attachAggregationCountFrom(
                              searchAggregations.carTypes
                          ),
                          carTypes
                      )
                    : undefined,
                productionYears: searchAggregations && searchAggregations.productionYears
                    ? searchAggregations.productionYears.map(entry => ({
                          ...entry,
                          key: Number(entry.key)
                      }))
                    : undefined,
                // @ts-ignore
                paginatedTags: searchAggregations && helpers.chunkify(
                    // @ts-ignore
                    props.searchAggregations.tags,
                    12
                ),
                paginatedKvps: helpers.chunkify(
                    props.searchAggregations.kvps,
                    12
                )
            });
        }
    };

    useEffect(() => {
        const { searchAggregations, selectedOptions } = props;

        if (props.selectedOptions) {
            // @ts-ignore
            setSelectedOptions(selectedOptions);
        }

        if (searchAggregations) {
            buildFilterOptions();
        }
    }, []);

    useEffect(() => {
        buildFilterOptions();
    }, [props.searchAggregations]);

    useEffect(() => {
        props.selectedOptions && setSelectedOptions(props.selectedOptions);
    }, [props.selectedOptions]);

    /**
     * This function combines API carModels with elastic search aggregation counts.
     * Furthermore, it updates each carModel.carType with aggregation count for this specific carType
     */

    const clearFilterInState = key => {
        const newState = {};
        newState[key] = [];
    };

    const onYearsUpdate = (low: number, high: number, event) => {
        /**
         * For unknown reason the title property of Material UI slider is not being passed
         * to this event handler. Thus, we assign that property here.
         */
        event.target.title = 'awc-slider';
        setSelectedOptions({
            ...selectedOptions,
            modelYears: { low, high }
        });

        props.onChange({
            ...selectedOptions,
            modelYears: { low, high }
        }, event);
    };

    const handleCarModelAndTypeSelection = (
        key: keyof ElasticAggregations,
        value: string | number,
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        /**
         * Temporarily store currentTarget, as its value may change,
         * during event bubbling even when event is persisted.
         * We want to persist the actual element that originated the event
         */
        const target = event.currentTarget;

        /**
         * For now we only support single selection of a model.
         */
        const next = { ...selectedOptions };

        /**
         * Deselect previous carTypes.
         */
        if (key === 'carModels') {
            next.carTypes = [];
        }

        if (selectedOptions[key].includes(value)) {
            next[key] = [];
        } else {
            next[key] = [value];
        }

        setSelectedOptions({ ...next });
        props.onChange(next, {
            ...event,
            currentTarget: target
        });
    };

    const handleMultiSelection = (key: string, nextSelectedOptions) => {
        const next = { ...selectedOptions };

        next[key] = nextSelectedOptions;
        setSelectedOptions({ ...next });
        props.onChange({ ...next });
    };

    const handleApplyFilters = () => {
        if (props.onApplyFilters) {
            props.onApplyFilters(selectedOptions);
        }
    };

    const onCancelFilter = () => {
        clearFilterInState('selectedOptions');
        if (props.onClose) {
            props.onClose();
        }
    };

    const onResetFilter = () => {
        setSelectedOptions({
            kvps: [],
            carTypes: [],
            fileExtensions: [],
            tags: [],
            carModels: [],
            modelYears: { low: null, high: null }
        });
        if (props.onResetFilters) {
            props.onResetFilters();
        }
    };

    const renderModelYearsSlider = () => {
        const currentYear = new Date().getFullYear();
        const productionYears = options.productionYears || [];
        const filterModelYears = selectedOptions.modelYears;
        const min =
            helpers.getMinYear(productionYears) < currentYear - 19
                ? helpers.getMinYear(productionYears) - 1
                : currentYear - 19;
        const max = currentYear + 1;
        const low =
            filterModelYears?.low || helpers.getMinYear(productionYears);
        const high =
            filterModelYears?.high || helpers.getMaxYear(productionYears);

        return (
            <div
                className="years-container"
                hidden={productionYears.length < 2}
                id="years"
                key="years"
                data-tab-label={t('filter_years')}
            >
                <h4 className="header">{t('filter_select_years')}</h4>
                <Slider
                    min={min}
                    max={max}
                    high={high}
                    low={low}
                    onChange={onYearsUpdate}
                />
            </div>
        );
    };

    const renderFileExtensions = (
        checkboxGroups,
        extraClass,
        key,
        pageTitle,
        fixedValues,
        optionKey
    ) => {
        return (
            <Pages
                className={extraClass}
                hidden={
                    Object.keys(checkboxGroups).length < 1 &&
                    typeof fixedValues === 'undefined'
                }
                key={key}
                data-tab-label={t(pageTitle)}
                isFullWidthChildren
            >
                <div className="checkbox-categories-container">
                    {Object.keys(checkboxGroups).map(groupKey => {
                        const options = checkboxGroups[groupKey];
                        const fixedValueForGrid = fixedValues[groupKey];
                        return (
                            <div
                                key={groupKey}
                                className="sub-category-container"
                            >
                                <h3 className="category-title">
                                    {t(groupKey)}
                                </h3>
                                <CheckboxGrid
                                    fixedValues={fixedValueForGrid.filter(
                                        opt => opt.key
                                    )}
                                    withHint={false}
                                    alwaysEnabled
                                    key={groupKey}
                                    gridKey={key}
                                    options={options.filter(opt => opt.key)}
                                    optionKey={optionKey}
                                    selectedOptions={selectedOptions[key]}
                                    onChange={handleMultiSelection}
                                />
                            </div>
                        );
                    })}
                </div>
            </Pages>
        );
    };

    const renderTags = (
        checkboxGrids: Array<
            SearchFilterState['options']['carTypes' | 'carModels' | 'tags']
        >,
        extraClass: string,
        key: string,
        pageTitle: string
    ) => {
        const generateKey = (keyValue, options) =>
            isEmpty(options)
                ? keyValue
                : keyValue + options[0].key + options[0].count;

        if (checkboxGrids[0]) {
            return (
                <Pages
                    className={extraClass}
                    hidden={checkboxGrids[0].length < 1}
                    key={key}
                    data-tab-label={t(pageTitle)}
                    isFullWidthChildren
                >
                    {checkboxGrids.length > 0 &&
                        checkboxGrids.map(options => (
                            <CheckboxGrid
                                selectedOptions={selectedOptions[key]}
                                gridKey={key}
                                key={generateKey(key, options)}
                                onChange={handleMultiSelection}
                                labelTransformer={(option) => normalizeTag(option.key as string)}
                                options={options as any}
                                isLeftAligned
                                withHint
                            />
                        ))}
                </Pages>
            );
        }
    };

    const renderKvps = (
        checkboxGrids: Array<
            SearchFilterState['options']['carTypes' | 'carModels' | 'tags' | 'kvps']
        >,
        extraClass: string,
        key: string,
        pageTitle: string
    ) => {
        const generateKey = (keyValue, options) =>
            isEmpty(options)
                ? keyValue
                : keyValue + options[0].key + options[0].count;
        if (checkboxGrids[0]) {
            return (
                <Pages
                    className={extraClass}
                    hidden={checkboxGrids[0].length < 1}
                    key={key}
                    data-tab-label={t(pageTitle)}
                    isFullWidthChildren
                >
                    {checkboxGrids.length > 0 &&
                        checkboxGrids.map(options => (
                            <CheckboxGrid
                                selectedOptions={selectedOptions[key]}
                                gridKey={key}
                                key={generateKey(key, options)}
                                onChange={handleMultiSelection}
                                options={options as any}
                                isLeftAligned
                                withHint
                            />
                        ))}
                </Pages>
            );
        }
    };

    const renderCarModels = () => {
        return (
            <CarModelSelector
                options={options}
                onSelect={handleCarModelAndTypeSelection}
                selectedOptions={selectedOptions}
                data-tab-label={t('filter_models')}
                isLoading={props.isSearching}
                isScrollIntoViewEnabled={
                    props.isScrollIntoViewEnabled || props.isSimplifiedView
                }
            />
        );
    };

    const renderExpiredMediaCheckbox = () => (
        <div
            key="expired"
            className="expired-media"
            id="expired"
            data-tab-label={t('filter_expired_media')}
        >
            <FilterImageCheckbox
                label={t('expired_media_only')}
                value="expired"
                check={props.config.isExpiredMediaChecked || false}
                onCheck={props.onShowExpiredMediaOnly}
                disabled={false}
            />
        </div>
    );

    const renderManagedMediaCheckbox = () => (
        <div
            key="ManagedMedia"
            className="ManagedMedia"
            id="ManagedMedia"
            data-tab-label={t('managed_media')}
        >
            <FilterImageCheckbox
                label={t('managed_media_only')}
                value="ManagedMedia"
                check={props.config.isManagedMediaChecked || false}
                onCheck={props.onShowManagedMediaOnly}
                disabled={false}
            />
        </div>
    );

    const renderDealerships = () => {
        // @ts-ignore
        const paginatedDealerships = helpers.chunkify(options.dealerships, 20);

        return (
            <Pages
                hidden={paginatedDealerships[0].length < 1}
                data-tab-label={t('filter_dealerships')}
                isFullWidthChildren
            >
                {options.dealerships &&
                    options.dealerships.length > 0 &&
                    paginatedDealerships.map(dealerships => (
                        <CheckboxGrid
                            selectedOptions={
                                selectedOptions.dealerships as string[]
                            }
                            gridKey="dealerships"
                            key={dealerships[0].kvps}
                            onChange={handleMultiSelection}
                            options={dealerships}
                            labelTransformer={option =>
                                `${option.key} - ${option.title}`
                            }
                            withHint
                        />
                    ))}
            </Pages>
        );
    };

    const {
        config,
        isSearching,
        isSimplifiedView,
        onClose,
        primaryButtonText,
        secondaryButtonText,
        onApplyFilters
    } = props;

    const tabs = [
        options.carModels && renderCarModels(),
        options.tags &&
            renderTags(
                options.paginatedTags,
                'is-left-aligned',
                'tags',
                'filter_tags'
            ),
        options.fileExtensions &&
            renderFileExtensions(
                options.fileExtensions,
                'is-left-aligned',
                'fileExtensions',
                t('filter_file_extensions'),
                options.fileExtensions,
                'key'
            ),
        options.productionYears && renderModelYearsSlider(),
        options.kvps &&
            renderKvps(
                options.paginatedKvps,
                'is-left-aligned',
                'kvps',
                'dealer_content'
            ),
        options.dealerships && renderDealerships(),
        config.isExpiredMediaTabVisible && renderExpiredMediaCheckbox(),
        config.isManagedMediaVisible && renderManagedMediaCheckbox()
    ].filter(Boolean) as React.ReactNode[];

    return (
        <div className="awc-search-filter">
            {isLoading ? (
                <div className="search-filter__loader-wrapper">
                    <Loader isInline />
                </div>
            ) : (
                <>
                    <ErrorBoundary
                        fallback={
                            <div className="search-filter__fallback-message">
                                No filter options available.
                            </div>
                        }
                    >
                        {isSimplifiedView ? (
                            renderCarModels()
                        ) : (
                            <Tabs>{tabs}</Tabs>
                        )}
                        <div className="awc-filter-footer">
                            {not(isSimplifiedView) && (
                                <button
                                    className="awc-filter-reset"
                                    onClick={onResetFilter}
                                    type="button"
                                >
                                    <img
                                        src={TrashIcon}
                                        className="trash-icon"
                                    />
                                    {t('filter_reset')}
                                </button>
                            )}
                            <ModalActionButtons
                                primaryButtonText={
                                    primaryButtonText || t('filter_apply')
                                }
                                secondaryButtonText={
                                    secondaryButtonText || t('filter_cancel')
                                }
                                onAction={
                                    onApplyFilters ? handleApplyFilters : null
                                }
                                onCancel={onClose ? onCancelFilter : null}
                                isLoading={isSearching}
                                style={{ marginLeft: 'auto' }}
                            />
                        </div>
                    </ErrorBoundary>
                    {not(isSimplifiedView) && (
                        <FilterCloseButton onClick={onClose || (() => null)} />
                    )}
                </>
            )}
            <div className="b-search-filter__divider" />
        </div>
    );
};
